import { Col, Flex, Row, Skeleton } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import DataItemComponent from './../components/dataItem';
import LatestActionsListComponent from '../components/latestActionsList';
import NotificationService from 'core/services/notification';
import { onGetCompanyOverview } from '../services/overviewServices';
import { CompanyOverview, LatestAction } from '../models/overview';
import Riyal from 'components/riyal/riyal';
import useCooperative from 'core/hooks/useCooperative';

interface CompanyOverviewPageProps {}

const CompanyOverviewPage: FunctionComponent<CompanyOverviewPageProps> = () => {
	const { t } = useTranslation();
	const { isCooperative } = useCooperative();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [companyOverview, setCompanyOverview] = useState<CompanyOverview>();
	const [latestActions, setLatestActions] = useState<LatestAction[]>([]);

	useEffect(() => {
		getCompanyOverview();
	}, []);

	const getCompanyOverview = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCompanyOverview();
			setCompanyOverview(response.company_overview);
			setLatestActions(response.latest_actions);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	return (
		<Row gutter={[16, 16]}>
			<Col span={24} lg={10}>
				<Row gutter={[16, 16]}>
					<Col span={24} lg={12}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent
								text={t('COMPANY_INFORMATION.TOTAL_COMMON_SHARES')}
								data={companyOverview?.number_of_shares.toLocaleString() || 0}
							/>
						)}
					</Col>
					<Col span={24} lg={12}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent text={isCooperative ? t('DASHBOARD.SHAREHOLDERS') : t('COMMON.SHAREHOLDERS')} data={companyOverview?.shareholders_count.toLocaleString() || 0} />
						)}
					</Col>
					<Col span={24}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent
								text={
									<Flex gap={5} align="center">
										{t('COMPANY_INFORMATION.COMPANY_CAPITAL_IN_SAR')}
										<Riyal color='#aaa'/>
									</Flex>
								}
								data={companyOverview?.capital.toLocaleString() || 0}
							/>
						)}
					</Col>
					<Col span={24}>
						<LatestActionsListComponent loading={isLoading} latestActions={latestActions} />
					</Col>
				</Row>
			</Col>
			<Col span={24} lg={14}>
				<Row gutter={[16, 16]}>
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent text={t('COMMON.COMMON')} data={companyOverview?.common_shares_count.toLocaleString() || 0} />
							)}
						</Col>
					)}
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent text={t('COMMON.PREFERRED')} data={companyOverview?.preferred_shares_count.toLocaleString() || 0} />
							)}
						</Col>
					)}
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMMON.TREASURY_SHARES')}
									data={companyOverview?.total_number_of_treasury_shares.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					<Col span={24} lg={8}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent
								text={t('COMPANY_INFORMATION.COUNT_OF_FREE_COMMON_SHARES')}
								data={companyOverview?.free_common_shares_count.toLocaleString() || 0}
							/>
						)}
					</Col>
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMPANY_INFORMATION.COUNT_OF_FREE_PREFERRED_SHARES')}
									data={companyOverview?.free_preferred_shares_count.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMPANY_INFORMATION.GRANTED_REQUESTS')}
									data={companyOverview?.granted_requests.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					<Col span={24} lg={8}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent
								text={t('COMPANY_INFORMATION.COUNT_OF_SUSPENDED_COMMON_SHARES')}
								data={companyOverview?.suspended_common_shares_count.toLocaleString() || 0}
							/>
						)}
					</Col>
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMPANY_INFORMATION.COUNT_OF_SUSPENDED_PREFERRED_SHARES')}
									data={companyOverview?.suspended_preferred_shares_count.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMPANY_INFORMATION.GRANTED_TREASURY_SHARES')}
									data={companyOverview?.granted_treasury_shares.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					<Col span={24} lg={8}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent
								text={t('COMPANY_INFORMATION.COUNT_OF_MORTGAGED_COMMON_SHARES')}
								data={companyOverview?.mortgaged_common_shares_count.toLocaleString() || 0}
							/>
						)}
					</Col>
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMPANY_INFORMATION.COUNT_OF_MORTGAGED_PREFERRED_SHARES')}
									data={companyOverview?.mortgaged_preferred_shares_count.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					{!isCooperative && (
						<Col span={24} lg={8}>
							{isLoading ? (
								<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
							) : (
								<DataItemComponent
									text={t('COMPANY_INFORMATION.RESERVED_TREASURY_SHARES')}
									data={companyOverview?.reserved_treasury_shares.toLocaleString() || 0}
								/>
							)}
						</Col>
					)}
					<Col span={24}>
						{isLoading ? (
							<Skeleton.Button active block style={{ minHeight: 150, borderRadius: 'var(--radius)' }} />
						) : (
							<DataItemComponent
								text={t('COMPANY_INFORMATION.VAT_REGISTRATION_NUMBER')}
								data={companyOverview?.tax_number.toLocaleString() || 0}
								centered
							/>
						)}
					</Col>
				</Row>
			</Col>
		</Row>
	);
};

export default CompanyOverviewPage;
